
import { HeroSection } from '../sections/HeroSection'
import { ServiceCard, ServiceCardGrid } from '../ui/ServiceCard'
import { ResponsiveContainer, ResponsiveGrid } from '../layout/ResponsiveContainer'
import { ScrollReveal } from '../animations/ScrollReveal'
import { useResponsive } from '../../hooks/useResponsive'

// Mock service data
const services = [
  {
    title: 'HR Templates',
    description: 'Get your branded hiring page with professional templates designed to attract top talent.',
    pricing: 'From €1,000',
    ctaText: 'Get Your Branded Page',
    serviceType: 'hr' as const,
    features: [
      'Professional branded design',
      'Mobile-responsive templates',
      'SEO optimized pages',
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7V9C15 10.1 14.1 11 13 11V22H11V16H9V22H7V11C5.9 11 5 10.1 5 9V7H3V9C3 11.2 4.8 13 7 13V22H17V13C19.2 13 21 11.2 21 9Z"/>
      </svg>
    ),
  },
  {
    title: 'Compliance Services',
    description: 'Protect your business from EU recalls with comprehensive compliance and insurance solutions.',
    pricing: 'From €500/month',
    ctaText: 'Protect Your Business',
    serviceType: 'compliance' as const,
    isComingSoon: true,
    features: [
      'EU compliance monitoring',
      'Recall insurance coverage',
      'Legal documentation',
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.5 16,12.1 16,12.7V16.2C16,16.8 15.4,17.3 14.8,17.3H9.2C8.6,17.3 8,16.8 8,16.2V12.7C8,12.1 8.6,11.5 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,10V11.5H13.5V10C13.5,8.7 12.8,8.2 12,8.2Z"/>
      </svg>
    ),
  },
  {
    title: 'Trade Services',
    description: 'Secure verified rare-earth export permits and hardware brokerage for international trade.',
    pricing: 'Custom pricing',
    ctaText: 'Get Export Permits',
    serviceType: 'trade' as const,
    isComingSoon: true,
    features: [
      'Rare-earth export permits',
      'Hardware brokerage',
      'International compliance',
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z"/>
      </svg>
    ),
  },
  {
    title: 'Intelligence Services',
    description: 'Discover hiring companies instantly with AI-powered job lead discovery and market intelligence.',
    pricing: 'From €200/month',
    ctaText: 'Discover Opportunities',
    serviceType: 'intelligence' as const,
    isComingSoon: true,
    features: [
      'AI-powered job discovery',
      'Market intelligence reports',
      'Lead generation tools',
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
      </svg>
    ),
  },
]

export function ResponsiveDemo() {
  const { isMobile, isTablet, breakpoint } = useResponsive()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <HeroSection
        title="Unified Mernaki Platform"
        subtitle="🚀 Multi-Division B2B Solutions"
        description="Transform your business with our comprehensive suite of HR, Compliance, Trade, and Intelligence services. Built for the modern enterprise."
        primaryCTA={{
          text: 'Explore Solutions',
          onClick: () => console.log('Explore clicked'),
        }}
        secondaryCTA={{
          text: 'Get Quote',
          onClick: () => console.log('Quote clicked'),
        }}
      />

      {/* Device Info Section */}
      <section className="section-padding bg-white">
        <ResponsiveContainer>
          <ScrollReveal direction="up">
            <div className="text-center mb-12">
              <h2 className="text-section-title text-gray-900 mb-4">
                Responsive Design in Action
              </h2>
              <div className="inline-flex items-center gap-4 px-6 py-3 bg-gray-100 rounded-lg">
                <span className="text-sm font-medium text-gray-600">
                  Current breakpoint:
                </span>
                <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
                  {breakpoint} ({isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'})
                </span>
              </div>
            </div>
          </ScrollReveal>
        </ResponsiveContainer>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gray-50">
        <ResponsiveContainer>
          <ScrollReveal direction="up" className="text-center mb-12">
            <h2 className="text-section-title text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-body-large text-gray-600 max-w-2xl mx-auto">
              Comprehensive business solutions designed to scale with your needs.
              Each service is optimized for mobile, tablet, and desktop experiences.
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" className="mt-12">
            <ServiceCardGrid>
              {services.map((service, index) => (
                <ServiceCard
                  key={index}
                  {...service}
                  onClick={() => console.log(`${service.title} clicked`)}
                  onSwipeLeft={() => console.log(`Swiped left on ${service.title}`)}
                  onSwipeRight={() => console.log(`Swiped right on ${service.title}`)}
                />
              ))}
            </ServiceCardGrid>
          </ScrollReveal>
        </ResponsiveContainer>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-white">
        <ResponsiveContainer>
          <ScrollReveal direction="up" className="text-center mb-12">
            <h2 className="text-section-title text-gray-900 mb-4">
              Responsive Features
            </h2>
          </ScrollReveal>

          <ResponsiveGrid
            cols={{ default: 1, md: 2, lg: 3 }}
            gap="lg"
          >
            <ScrollReveal direction="up" delay={0}>
              <div className="card">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <h3 className="text-card-title text-gray-900 mb-2">Touch-Friendly</h3>
                <p className="text-body text-gray-600">
                  All interactive elements meet the 44px minimum touch target size for optimal mobile experience.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={0.1}>
              <div className="card">
                <div className="w-12 h-12 bg-hr-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-hr-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h3 className="text-card-title text-gray-900 mb-2">Swipe Gestures</h3>
                <p className="text-body text-gray-600">
                  Native swipe gestures for service cards on mobile devices with haptic feedback.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={0.2}>
              <div className="card">
                <div className="w-12 h-12 bg-intelligence-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-intelligence-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <h3 className="text-card-title text-gray-900 mb-2">Scroll Animations</h3>
                <p className="text-body text-gray-600">
                  Smooth scroll-triggered animations that respect user preferences for reduced motion.
                </p>
              </div>
            </ScrollReveal>
          </ResponsiveGrid>
        </ResponsiveContainer>
      </section>
    </div>
  )
}